import React from 'react';
import { motion } from 'framer-motion';
import { Compass, Target, CheckCircle, XCircle } from 'lucide-react';
export function SelfDirectionPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1849" data-mp="2x2azuikagkgr">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1850" data-mp="2s7x3actw47ai">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1851" data-mp="2n9vi9mcwlxwe">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1852" data-mp="29bneauhg9wbq">
            <span className="w-8 h-px bg-teal" data-id="element-1853" data-mp="24bsneqxod6qo"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1854" data-mp="24d3v31u8qyn2">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1855" data-mp="2k65rfzoao6v"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1856" data-mp="2kxovrc32tg9">
            Self-Direction and Execution
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1857" data-mp="j2bs8qr6s00d">
            At SyNeural, self-direction and execution refer to the ability to
            organize effort, follow through, maintain discipline, translate
            goals into action, and sustain progress over time without constant
            external pressure. It is the capability that turns intention into
            movement and potential into real output.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1858" data-mp="o03g99opn1za">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1859" data-mp="syests6e3ql7">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1860" data-mp="1vb7qhlv29bb7">
            This matters because many people have intelligence, creativity, or
            ambition without consistent execution. They may understand what
            should be done, but struggle to organize themselves, sustain
            momentum, or convert plans into results. Self-direction and
            execution therefore play a major role in whether potential becomes
            contribution, whether learning becomes action, and whether
            opportunity turns into progress.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1861" data-mp="xnzau7hqqwh4">
        <div className="max-w-4xl mx-auto" data-id="element-1862" data-mp="xm4pis2k52ci">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1863" data-mp="snte4cwm6js6">
            <div className="flex items-center gap-4 mb-6" data-id="element-1864" data-mp="34eayqvttvlqw">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1865" data-mp="39ccjrmatdv50">
                <Target size={24} data-id="element-1866" data-mp="3e92z27esompd" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1867" data-mp="2gmndouj8z4wj">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1868" data-mp="2lmi8vaa22gne">
              Without execution, many other strengths remain dormant. Weak
              self-direction can undermine education, work, leadership
              development, and personal stability even when talent is present.
              Strong execution improves reliability, momentum, confidence, and
              readiness for greater responsibility. It is one of the clearest
              bridges between capability and real-world performance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Self-Direction Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1869" data-mp="4blxip88vm4j">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1870" data-mp="i6ek27mjy2km">
          <div className="flex items-center gap-4 mb-6" data-id="element-1871" data-mp="n3n1u73sg4lc">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1872" data-mp="113xu9ekqxr8m">
              <CheckCircle size={24} data-id="element-1873" data-mp="w5o119il10pn" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1874" data-mp="32v2n2dyf0xuj">
              What Strong Self-Direction Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1875" data-mp="2xxixihw9oyna">
            People with strong self-direction and execution are more likely to
            set goals, create structure, follow through on commitments, manage
            time effectively, and keep moving even when motivation fluctuates.
            They do not depend entirely on pressure or supervision to act. They
            are better able to generate forward movement from within.
          </p>
        </motion.div>
      </section>

      {/* What Weak Self-Direction Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1876" data-mp="zm91yw6adui9">
        <div className="max-w-4xl mx-auto" data-id="element-1877" data-mp="lmr6xz2edl0g">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1878" data-mp="lnsuh30lnzz2">
            <div className="flex items-center gap-4 mb-6" data-id="element-1879" data-mp="2k01a2iqm5oac">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1880" data-mp="3t03rqlpk8zkv">
                <XCircle size={24} data-id="element-1881" data-mp="3nyo72uraov30" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1882" data-mp="3jy48rnuzjlfa">
                What Weak Self-Direction Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1883" data-mp="3f1man6u5ulbx">
              Weak self-direction often appears as inconsistency,
              procrastination, abandoned plans, chaotic routines, low
              follow-through, and difficulty converting ideas into sustained
              action. In many cases, the issue is not lack of ability but lack
              of structure, discipline, or reliable behavioral systems. Over
              time, this can create frustration, wasted talent, and
              underachievement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1884" data-mp="11tmnqt4kmh4x">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1885" data-mp="16rwkv99uvrt6">
          <div className="flex items-center gap-4 mb-6" data-id="element-1886" data-mp="1krz00nqs474q">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1887" data-mp="1krfhb4tb1epn">
              <Compass size={24} data-id="element-1888" data-mp="1popex7qukwok" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1889" data-mp="1uijrzsby6deo">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1890" data-mp="1ziek1z7gl91b">
            Self-direction and execution can be improved through habit systems,
            goal design, accountability structures, time management, coaching,
            milestone planning, and environments that reduce friction and
            support consistency. In the SyNeural framework, this capability is
            treated as crucial because judgment and talent only become
            meaningful when they can be translated into sustained action.
          </p>
        </motion.div>
      </section>
    </div>;
}